if (!customElements.get('entry-popup')) {
  customElements.define(
    'entry-popup',
    class EntryPopup extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.panel = this.querySelector('[data-entry-popup-panel]');
        this.overlay = this.querySelector('[data-entry-popup-overlay]');
        this.closeButtons = this.querySelectorAll('[data-entry-popup-close]');
        this.sectionId = this.dataset.sectionId || 'global';
        this.storageKey = 'entry-popup:' + this.sectionId;
        this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
        this.isOpen = false;

        this.onKeydown = this.handleKeydown.bind(this);
        this.onOverlayClick = this.handleOverlayClick.bind(this);
        this.onSectionSelect = this.handleSectionSelect.bind(this);
        this.onCloseClick = () => this.close();

        this.closeButtons.forEach((button) => button.addEventListener('click', this.onCloseClick));
        this.overlay?.addEventListener('click', this.onOverlayClick);
        document.addEventListener('keydown', this.onKeydown);
        document.addEventListener('shopify:section:select', this.onSectionSelect);

        if (!this.shouldDisplay()) return;

        const delay = this.designMode ? 0 : Math.max(0, Number(this.dataset.delay || 0) * 1000);
        this.showTimer = window.setTimeout(() => this.show(), delay);
      }

      disconnectedCallback() {
        window.clearTimeout(this.showTimer);
        window.clearTimeout(this.closeTimer);
        this.closeButtons?.forEach((button) => button.removeEventListener('click', this.onCloseClick));
        this.overlay?.removeEventListener('click', this.onOverlayClick);
        document.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('shopify:section:select', this.onSectionSelect);
        if (this.isOpen) document.body.classList.remove('entry-popup-open');
      }

      shouldDisplay() {
        if (this.dataset.enabled !== 'true') return false;
        if (this.dataset.homeOnly === 'true' && this.dataset.isHome !== 'true') return false;

        const mobile = window.matchMedia('(max-width: 749px)').matches;
        if (mobile && this.dataset.enableMobile !== 'true') return false;
        if (!mobile && this.dataset.enableDesktop !== 'true') return false;
        if (this.designMode) return true;

        try {
          if (this.dataset.frequency === 'session') {
            return sessionStorage.getItem(this.storageKey) !== 'shown';
          }

          if (this.dataset.frequency === 'days') {
            const lastShown = Number(localStorage.getItem(this.storageKey) || 0);
            const days = Math.max(1, Number(this.dataset.frequencyDays || 1));
            return !lastShown || Date.now() - lastShown >= days * 86400000;
          }
        } catch (error) {
          return true;
        }

        return true;
      }

      markShown() {
        if (this.designMode) return;

        try {
          if (this.dataset.frequency === 'session') {
            sessionStorage.setItem(this.storageKey, 'shown');
          } else if (this.dataset.frequency === 'days') {
            localStorage.setItem(this.storageKey, String(Date.now()));
          }
        } catch (error) {
          // Storage can be unavailable in strict privacy modes; the popup still works.
        }
      }

      show() {
        if (this.isOpen || !this.panel) return;

        this.previouslyFocused = document.activeElement;
        this.hidden = false;
        this.panel.setAttribute('aria-hidden', 'false');
        document.body.classList.add('entry-popup-open');
        this.isOpen = true;
        this.markShown();

        window.requestAnimationFrame(() => {
          this.classList.add('is-visible');
          const firstTarget =
            this.querySelector('[data-entry-popup-close]') ||
            this.querySelector('a[href], button:not([disabled])') ||
            this.panel;
          firstTarget.focus({ preventScroll: true });
        });

        const autoClose = Number(this.dataset.autoClose || 0);
        if (!this.designMode && autoClose > 0) {
          this.closeTimer = window.setTimeout(() => this.close(), autoClose * 1000);
        }
      }

      close() {
        if (!this.isOpen) return;

        window.clearTimeout(this.closeTimer);
        this.classList.remove('is-visible');
        this.panel.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('entry-popup-open');
        this.isOpen = false;

        window.setTimeout(() => {
          if (!this.isOpen) this.hidden = true;
        }, 240);

        if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
          this.previouslyFocused.focus({ preventScroll: true });
        }
      }

      handleOverlayClick(event) {
        if (this.dataset.dismissOverlay === 'true' && event.target === this.overlay) {
          this.close();
        }
      }

      handleSectionSelect(event) {
        if (event.detail && event.detail.sectionId === this.sectionId) {
          window.clearTimeout(this.showTimer);
          this.show();
        }
      }

      handleKeydown(event) {
        if (!this.isOpen) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }

        if (event.key !== 'Tab') return;

        const focusable = Array.from(
          this.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((element) => element.offsetParent !== null);

        if (!focusable.length) {
          event.preventDefault();
          this.panel.focus();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
}
